import type { AxiosError } from 'axios';

const FREIGHT_TYPE_LABELS: Record<string, string> = {
  '1': 'Frete Grátis',
  '2': 'Valor Fixo',
  '3': 'Correios',
  '4': 'Melhor Envio'
};

const PACKING_FORMAT_LABELS: Record<string, string> = {
  '1': 'Caixa/Pacote',
  '2': 'Rolo/Prisma'
};

const FIELD_LABELS: Record<string, string> = {
  'type delivery': 'tipo de entrega',
  'type_delivery': 'tipo de entrega',
  'deadline delivery': 'prazo de entrega',
  'deadline_delivery': 'prazo de entrega',
  'freight value': 'valor do frete',
  'freight_value': 'valor do frete',
  'type freight': 'tipo de frete',
  'type_freight': 'tipo de frete',
  'packing': 'formato de embalagem',
  'formato de embalagem': 'formato de embalagem',
  'cep origin': 'CEP de origem',
  'cep_origin': 'CEP de origem',
  'peso': 'peso',
  'comprimento': 'comprimento',
  'largura': 'largura',
  'altura': 'altura',
  'diametro': 'diâmetro',
  'diameter': 'diâmetro',
  'weight': 'peso',
  'height': 'altura',
  'width': 'largura',
  'length': 'comprimento'
};

const FIELD_FRIENDLY_MESSAGES: Record<string, string> = {
  'type delivery': 'Selecione ao menos uma opção de entrega: PAC, SEDEX ou SEDEX 10',
  'type_delivery': 'Selecione ao menos uma opção de entrega: PAC, SEDEX ou SEDEX 10',
  'deadline delivery': 'Informe o prazo de entrega (em dias)',
  'deadline_delivery': 'Informe o prazo de entrega (em dias)',
  'freight value': 'Informe o valor do frete',
  'freight_value': 'Informe o valor do frete',
  'packing': 'Selecione o formato de embalagem',
  'formato de embalagem': 'Selecione o formato de embalagem',
  'cep origin': 'Informe o CEP de origem',
  'cep_origin': 'Informe o CEP de origem',
  'peso': 'Informe o peso do produto',
  'weight': 'Informe o peso do produto',
  'comprimento': 'Informe o comprimento da embalagem',
  'length': 'Informe o comprimento da embalagem',
  'largura': 'Informe a largura da embalagem',
  'width': 'Informe a largura da embalagem',
  'altura': 'Informe a altura da embalagem',
  'height': 'Informe a altura da embalagem',
  'diametro': 'Informe o diâmetro da embalagem',
  'diameter': 'Informe o diâmetro da embalagem'
};

const normalizeFieldKey = (field: string) => field.trim().toLowerCase().replace(/_/g, ' ');

const resolveFieldKey = (field: string) => {
  const normalized = normalizeFieldKey(field);
  const underscored = field.trim().toLowerCase().replace(/\s+/g, '_');

  if (FIELD_FRIENDLY_MESSAGES[normalized] || FIELD_LABELS[normalized]) return normalized;
  if (FIELD_FRIENDLY_MESSAGES[underscored] || FIELD_LABELS[underscored]) return underscored;
  if (FIELD_FRIENDLY_MESSAGES[field] || FIELD_LABELS[field]) return field;

  return normalized;
};

const buildRequiredMessage = (field: string, contextLabel?: string) => {
  const fieldKey = resolveFieldKey(field);
  const specificMessage = FIELD_FRIENDLY_MESSAGES[fieldKey];
  const fieldLabel = FIELD_LABELS[fieldKey] ?? field;

  if (specificMessage && contextLabel) {
    return `${specificMessage} para ${contextLabel}`;
  }

  if (specificMessage) {
    return specificMessage;
  }

  if (contextLabel) {
    return `O campo ${fieldLabel} é obrigatório para ${contextLabel}`;
  }

  return `O campo ${fieldLabel} é obrigatório`;
};

const toFriendlyMessage = (message: string) => {
  const freightRequiredMatch = message.match(/O campo\s+(.+?)\s+é obrigatório quando type\s*_?freight for\s+(\d+)\.?/i);

  if (freightRequiredMatch) {
    const freightLabel = FREIGHT_TYPE_LABELS[freightRequiredMatch[2]] ?? 'o tipo de frete selecionado';
    return buildRequiredMessage(freightRequiredMatch[1], freightLabel);
  }

  const packingRequiredMatch = message.match(
    /O campo\s+(.+?)\s+é obrigatório quando (?:formato de embalagem|packing) for\s+(\d+)\.?/i
  );

  if (packingRequiredMatch) {
    const packingLabel = PACKING_FORMAT_LABELS[packingRequiredMatch[2]] ?? 'o formato de embalagem selecionado';
    return buildRequiredMessage(packingRequiredMatch[1], packingLabel);
  }

  const requiredMatch = message.match(/O campo\s+(.+?)\s+é obrigatório\.?$/i);

  if (requiredMatch) {
    return buildRequiredMessage(requiredMatch[1]);
  }

  let friendly = message;

  Object.entries(FIELD_LABELS).forEach(([technical, label]) => {
    friendly = friendly.replace(new RegExp(technical.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), label);
  });

  Object.entries(FREIGHT_TYPE_LABELS).forEach(([code, label]) => {
    friendly = friendly.replace(new RegExp(`tipo de frete for\\s*${code}`, 'gi'), label);
    friendly = friendly.replace(new RegExp(`type freight for\\s*${code}`, 'gi'), label);
  });

  Object.entries(PACKING_FORMAT_LABELS).forEach(([code, label]) => {
    friendly = friendly.replace(new RegExp(`formato de embalagem for\\s*${code}`, 'gi'), label);
    friendly = friendly.replace(new RegExp(`packing for\\s*${code}`, 'gi'), label);
  });

  return friendly;
};

export const formatterApiErrors = (err: AxiosError<any>): string => {
  const message = err?.response?.data?.message;

  if (!message) {
    return 'Algo deu errado, tente novamente...';
  }

  if (typeof message === 'string') {
    return toFriendlyMessage(message);
  }

  if (typeof message.message === 'string') {
    return toFriendlyMessage(message.message);
  }

  if (typeof message.error === 'string') {
    return toFriendlyMessage(message.error);
  }

  if (typeof message.error?.customer === 'string') {
    return toFriendlyMessage(message.error.customer);
  }

  if (typeof message.error?.title === 'string') {
    return toFriendlyMessage(message.error.title);
  }

  if (typeof message.error === 'object' && typeof message.error[Object.keys(message.error)[0]] === 'string') {
    return toFriendlyMessage(message.error[Object.keys(message.error)[0]]);
  }

  if (Array.isArray(message.error) && typeof message.error[0] === 'string') {
    return toFriendlyMessage(message.error[0]);
  }

  return 'Algo deu errado, tente novamente...';
};

export default formatterApiErrors;
